import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { UnifiedStatusBadge } from "@/components/shared/UnifiedStatusBadge";
import { useState } from "react";
import { motion } from "framer-motion";
import { Search, Heart, Clock, DollarSign, TrendingUp, Gift, Star, Sparkles, ChevronRight, Filter } from "lucide-react";

const MiniLogo = ({ name, size = "md" }) => {
  const colors = ["bg-sky-400", "bg-purple-400", "bg-green-400", "bg-orange-400", "bg-pink-400", "bg-indigo-400", "bg-teal-400", "bg-red-400"];
  const i = name ? name.charCodeAt(0) % colors.length : 0;
  const sizeClass = size === "sm" ? "w-8 h-8 text-xs" : size === "lg" ? "w-12 h-12 text-lg" : "w-10 h-10 text-sm";
  return (
    <div className={`${sizeClass} rounded-xl ${colors[i]} flex items-center justify-center flex-shrink-0`}>
      <span className="text-white font-bold">{name?.charAt(0)?.toUpperCase()}</span>
    </div>
  );
};

const BentoCell = ({ children, className = "", span = "", onClick, testId }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    whileHover={{ scale: 1.02 }}
    onClick={onClick}
    className={`bg-white/70 backdrop-blur-xl rounded-2xl border border-white/50 shadow-[0_4px_20px_rgba(0,0,0,0.06)] overflow-hidden ${span} ${className} ${onClick ? "cursor-pointer" : ""}`}
    data-testid={testId}
  >
    {children}
  </motion.div>
);

export const BentoView = ({ data, user, onOfferClick }) => {
  const { getStatus } = useUnifiedStatus();
  const { offers, expiringSoon, bestValue, stats, favoriteIds, toggleFavorite, categories, selectedCategory, setSelectedCategory, searchQuery, setSearchQuery } = data;

  const totalValue = offers.reduce((sum, o) => sum + (o.offer_value_est || 0), 0);

  // Category distribution for mini chart
  const catCounts = {};
  offers.forEach(o => { catCounts[o.category] = (catCounts[o.category] || 0) + 1; });
  const topCats = Object.entries(catCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const maxCount = Math.max(...topCats.map(c => c[1]), 1);

  return (
    <div className="min-h-screen bg-slate-100 pb-28" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div className="px-6 pt-8 pb-4">
        <p className="text-slate-400 text-xs font-medium tracking-widest uppercase">Dashboard</p>
        <h1 className="text-2xl font-extrabold text-slate-900 mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Hi, {user?.first_name}
        </h1>
      </div>

      {/* Search bar */}
      <div className="px-6 mb-4">
        <div className="bg-white/70 backdrop-blur-xl rounded-xl flex items-center gap-2 px-4 py-3 border border-white/50 shadow-sm">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search deals..."
            className="flex-1 bg-transparent text-sm text-slate-800 outline-none placeholder-slate-400"
            data-testid="bento-search"
          />
        </div>
      </div>

      {/* Filter pills */}
      <div className="px-6 mb-4 flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        <button
          onClick={() => setSelectedCategory([])}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
            selectedCategory.length === 0 ? "bg-sky-500 text-white shadow-md shadow-sky-500/20" : "bg-white text-slate-500 border border-slate-200"
          }`}
          data-testid="bento-filter-all"
        >
          All
        </button>
        {categories.filter(c => c !== "All").slice(0, 6).map(cat => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(selectedCategory.includes(cat) ? selectedCategory.filter(c => c !== cat) : [...selectedCategory, cat])}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
              selectedCategory.includes(cat) ? "bg-sky-500 text-white shadow-md shadow-sky-500/20" : "bg-white text-slate-500 border border-slate-200"
            }`}
            data-testid={`bento-filter-${cat.toLowerCase().replace(/\s/g, '-')}`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Bento Grid */}
      <div className="px-6 grid grid-cols-2 gap-3">
        {/* Big: Total Value */}
        <BentoCell span="col-span-1" testId="bento-value">
          <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 h-full">
            <DollarSign className="w-5 h-5 text-green-500 mb-2" />
            <p className="text-3xl font-extrabold text-green-600" style={{ fontFamily: "'Outfit', sans-serif" }}>
              ${totalValue.toFixed(0)}
            </p>
            <p className="text-xs text-green-500 mt-1">Birthday Haul</p>
          </div>
        </BentoCell>

        {/* Stats */}
        <BentoCell span="col-span-1" testId="bento-stats">
          <div className="p-4 h-full flex flex-col justify-between">
            <div className="flex items-center gap-2">
              <Gift className="w-4 h-4 text-sky-500" />
              <span className="text-xs text-slate-400">Overview</span>
            </div>
            <div className="mt-2 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs text-slate-500">Offers</span>
                <span className="text-sm font-bold text-slate-800">{offers.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-slate-500">Days Left</span>
                <span className="text-sm font-bold text-sky-600">{stats.days_until_birthday}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-slate-500">Expiring</span>
                <span className="text-sm font-bold text-red-500">{expiringSoon.length}</span>
              </div>
            </div>
          </div>
        </BentoCell>

        {/* Expiring Soon — wide */}
        {expiringSoon.length > 0 && (
          <BentoCell span="col-span-2" testId="bento-expiring">
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-red-500" />
                <h3 className="text-sm font-bold text-slate-800">Expiring Soon</h3>
              </div>
              <div className="flex gap-3 overflow-x-auto scrollbar-hide -mx-1 px-1 pb-1">
                {expiringSoon.slice(0, 4).map(offer => (
                  <div
                    key={offer.business_id}
                    onClick={() => onOfferClick(offer)}
                    className="flex-shrink-0 flex items-center gap-2.5 bg-red-50 rounded-xl px-3 py-2.5 border border-red-100 cursor-pointer hover:shadow-sm transition-shadow"
                    data-testid={`bento-exp-${offer.business_id}`}
                  >
                    <MiniLogo name={offer.name} size="sm" />
                    <div>
                      <p className="text-xs font-semibold text-slate-800">{offer.name}</p>
                      {getStatus(offer.business_id) && <UnifiedStatusBadge status={getStatus(offer.business_id)} />}
                      <p className="text-xs text-green-600 font-bold">${offer.offer_value_est}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </BentoCell>
        )}

        {/* Category mini-chart */}
        <BentoCell span="col-span-1" testId="bento-categories">
          <div className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-indigo-500" />
              <span className="text-xs text-slate-400">Categories</span>
            </div>
            <div className="space-y-2">
              {topCats.map(([cat, count]) => (
                <div key={cat} className="flex items-center gap-2">
                  <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(count / maxCount) * 100}%` }}
                      className="h-full bg-indigo-400 rounded-full"
                    />
                  </div>
                  <span className="text-[10px] text-slate-500 w-14 text-right truncate">{cat}</span>
                </div>
              ))}
            </div>
          </div>
        </BentoCell>

        {/* Best Value — single featured */}
        {bestValue.length > 0 && (
          <BentoCell
            span="col-span-1"
            onClick={() => onOfferClick(bestValue[0])}
            testId="bento-best-value"
          >
            <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 h-full">
              <Star className="w-4 h-4 text-amber-500 mb-2" />
              <p className="text-xs text-amber-600 font-medium">Best Value</p>
              <MiniLogo name={bestValue[0].name} size="sm" />
              <p className="text-sm font-bold text-slate-800 mt-2 truncate">{bestValue[0].name}</p>
              <p className="text-lg font-extrabold text-amber-600">${bestValue[0].offer_value_est}</p>
            </div>
          </BentoCell>
        )}

        {/* All Offers — compact list */}
        <BentoCell span="col-span-2" testId="bento-offers-list">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-slate-800">All Birthday Deals</h3>
              <span className="text-xs text-slate-400">{offers.length} total</span>
            </div>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {offers.slice(0, 15).map(offer => (
                <div
                  key={offer.business_id}
                  onClick={() => onOfferClick(offer)}
                  className="flex items-center gap-3 py-2 px-2 rounded-xl hover:bg-slate-50 cursor-pointer transition-colors"
                  data-testid={`bento-offer-${offer.business_id}`}
                >
                  <MiniLogo name={offer.name} size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{offer.name}</p>
                    <p className="text-xs text-slate-400 truncate">{offer.category}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-green-600">${offer.offer_value_est}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleFavorite(offer.business_id); }}
                      className="p-0.5"
                    >
                      <Heart className={`w-3.5 h-3.5 ${favoriteIds.has(offer.business_id) ? "fill-red-500 text-red-500" : "text-slate-200"}`} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {offers.length > 15 && (
              <p className="text-center text-xs text-sky-500 font-medium mt-3">Scroll for more</p>
            )}
          </div>
        </BentoCell>
      </div>
    </div>
  );
};
